<template>
    <ProductSelectModalOverlay @click.self="closeModal">
        <ProductSelectModalContainer>
            <ProductSelectModalHeader>
                <ProductSelectModalTitle>재고에서 품목 선택</ProductSelectModalTitle>
                <CloseButton @click="closeModal">
                    <Close :size="24" />
                </CloseButton>
            </ProductSelectModalHeader>

            <ProductSelectModalContent>
                <!-- 카테고리 선택 -->
                <CategorySelectSection>
                    <CategorySelectLabel>카테고리 선택</CategorySelectLabel>
                    <CategorySelect v-model="selectedCategory" @change="onCategoryChange">
                        <option value="">전체</option>
                        <option v-for="cat in categoryList" :key="cat.num" :value="cat.num">
                            {{ cat.name }}
                        </option>
                    </CategorySelect>
                </CategorySelectSection>

                <!-- 검색 및 재고 목록 -->
                <ProductListSection>
                    <ProductListHeader>
                        <LeftSection>
                            <ProductListTitle>재고 목록</ProductListTitle>
                            <SearchInputWrapper>
                                <SearchInput v-model="searchInput" placeholder="품목명 검색 후 엔터"
                                    @keyup.enter="performSearch" />
                                <SearchButton @click="performSearch">
                                    <Magnify :size="20" />
                                </SearchButton>
                            </SearchInputWrapper>
                        </LeftSection>
                        <ProductCount>총 {{ filteredProducts.length }}개</ProductCount>
                    </ProductListHeader>

                    <ProductTable v-if="!loadingProducts && filteredProducts && filteredProducts.length > 0">
                        <ProductTableHeader>
                            <div>카테고리</div>
                            <div>품목명</div>
                            <div>수량</div>
                            <div>가격</div>
                        </ProductTableHeader>
                        <ProductTableBody>
                            <ProductTableRow v-for="product in filteredProducts" :key="product.num"
                                @click="addProductToSet(product)" style="cursor: pointer;">
                                <div>{{ product.name0 || '-' }}</div>
                                <div>{{ product.name3 || product.name || '-' }}</div>
                                <div>
                                    <input type="number" min="1" :value="getProductQuantity(product)" @click.stop
                                        @input="updateProductQuantity(product, $event.target.value)"
                                        style="width: 60px; padding: 4px;" />
                                </div>
                                <div>
                                    <input type="number" min="0" :value="getProductPrice(product)" @click.stop
                                        @input="updateProductPrice(product, $event.target.value)"
                                        style="width: 100px; padding: 4px;" />
                                </div>
                            </ProductTableRow>
                        </ProductTableBody>
                    </ProductTable>

                    <LoadingMessage v-if="loadingProducts">재고를 불러오는 중...</LoadingMessage>
                    <EmptyMessage v-if="!loadingProducts && (!filteredProducts || filteredProducts.length === 0)">
                        {{ searchQuery ? '검색 결과가 없습니다.' : '선택한 카테고리에 재고가 없습니다.' }}
                    </EmptyMessage>
                </ProductListSection>
            </ProductSelectModalContent>

            <ProductSelectModalFooter>
                <ModalButton @click="closeModal">닫기</ModalButton>
            </ProductSelectModalFooter>
        </ProductSelectModalContainer>
    </ProductSelectModalOverlay>
</template>

<script>
import * as ProductSelectModalCss from '@/assets/styles/product/modal/ProductSelectModalCss.js';
import Close from 'vue-material-design-icons/Close.vue';
import Magnify from 'vue-material-design-icons/Magnify.vue';

export default {
    name: 'ProductSelectModal',

    components: {
        ...ProductSelectModalCss,
        Close, Magnify
    },

    props: {
        categoryList: {
            type: Array,
            default: () => []
        },
        companykey: {
            type: [String, Number],
            required: true
        }
    },

    emits: ['close', 'add-products'],

    data() {
        return {
            selectedCategory: '', // 초기값을 빈 문자열로 설정하여 "전체" 선택
            productsByCategory: {},
            allProducts: [], // 전체 재고 저장
            productQuantities: {}, // 각 제품의 수량 저장
            productPrices: {}, // 각 제품의 가격 저장
            currentPage: 1,
            totalPages: 1,
            loadingProducts: false,
            searchInput: '', // 입력 필드의 검색어
            searchQuery: '' // 실제 필터링에 사용되는 검색어
        }
    },

    computed: {
        currentProducts() {
            if (!this.selectedCategory) {
                // 전체 카테고리 선택 시 모든 재고 반환
                return this.allProducts;
            }
            const categoryKey = this.selectedCategory.toString();
            return this.productsByCategory[categoryKey] || [];
        },
        filteredProducts() {
            if (!this.searchQuery.trim()) {
                return this.currentProducts;
            }
            const query = this.searchQuery.toLowerCase().trim();
            return this.currentProducts.filter(product => {
                const name0 = (product.name0 || '').toLowerCase();
                const name3 = (product.name3 || '').toLowerCase();
                const name = (product.name || '').toLowerCase();
                return name0.includes(query) || name3.includes(query) || name.includes(query);
            });
        }
    },

    methods: {
        // 체크박스 관련 메서드 제거하고 row 클릭으로 바로 추가하는 메서드로 변경
        addProductToSet(product) {
            const category = this.categoryList.find(cat => {
                const catNum = typeof cat.num === 'string' ? parseInt(cat.num, 10) : cat.num;
                const selectedNum = typeof this.selectedCategory === 'string' ? parseInt(this.selectedCategory, 10) : this.selectedCategory;
                return catNum === selectedNum;
            });

            const productToAdd = {
                category: category ? category.num : '__custom__',
                customCategory: category ? '' : (product.name0 || ''),
                productName: product.name3 || product.name || '',
                productList: [],
                quantity: this.getProductQuantity(product),
                price: this.getProductPrice(product)
            };

            // 부모 컴포넌트로 전달 (단일 제품)
            this.$emit('add-products', [productToAdd]);

            // 성공 메시지
            this.$swal.fire({
                icon: 'success',
                title: '추가 완료',
                text: `${product.name3 || product.name || '품목'}이 추가되었습니다.`,
                timer: 1500,
                showConfirmButton: false,
                toast: true,
                position: 'top-end'
            });
        },

        getProductQuantity(product) {
            if (!this.productQuantities[product.num]) {
                this.productQuantities[product.num] = 1;
            }
            return this.productQuantities[product.num];
        },

        getProductPrice(product) {
            if (!this.productPrices[product.num]) {
                this.productPrices[product.num] = product.price || 0;
            }
            return this.productPrices[product.num];
        },

        updateProductQuantity(product, quantity) {
            this.productQuantities[product.num] = parseInt(quantity) || 1;
        },

        updateProductPrice(product, price) {
            this.productPrices[product.num] = parseFloat(price) || 0;
        },

        closeModal() {
            this.$emit('close');
        },

        async onCategoryChange() {
            if (!this.selectedCategory) {
                // 전체 카테고리 선택 시 모든 카테고리의 재고 로드
                await this.loadAllProducts();
                return;
            }
            this.currentPage = 1;
            const categoryId = parseInt(this.selectedCategory, 10);
            await this.loadProductsByCategory(categoryId);
        },

        onSearchInput() {
            // 검색어 입력 시 자동 필터링 (computed 속성으로 처리)
        },

        performSearch() {
            // 검색 실행: 입력된 검색어를 실제 검색어로 설정
            this.searchQuery = this.searchInput.trim();
        },

        async loadAllProducts() {
            try {
                this.loadingProducts = true;
                this.allProducts = []; // 전체 재고 초기화

                // 모든 카테고리의 재고를 병렬로 로드
                const loadPromises = this.categoryList.map(cat =>
                    this.loadProductsByCategory(cat.num, 1, true) // true: allProducts에 추가
                );

                await Promise.all(loadPromises);

                // 중복 제거
                this.allProducts = this.allProducts.filter((product, index, self) =>
                    index === self.findIndex(p => p.num === product.num)
                );
            } catch (error) {
                console.error('전체 재고 로드 실패:', error);
                this.$swal.fire({
                    icon: 'error',
                    title: '오류',
                    text: '재고를 불러오는 중 오류가 발생했습니다.'
                });
            } finally {
                this.loadingProducts = false;
            }
        },

        async loadProductsByCategory(categoryId, page = 1, addToAll = true) {
            try {
                if (page === 1) {
                    this.loadingProducts = true;
                }

                const numCategoryId = typeof categoryId === 'string' ? parseInt(categoryId, 10) : categoryId;

                const category = this.categoryList.find(cat => {
                    const catNum = typeof cat.num === 'string' ? parseInt(cat.num, 10) : cat.num;
                    return catNum === numCategoryId;
                });

                if (!category) {
                    console.error('카테고리를 찾을 수 없습니다:', numCategoryId);
                    return;
                }

                const requestData = {
                    page: page,
                    companykey: String(this.companykey),
                    category: category.name,
                    status: 0
                };

                const response = await this.$axios.post(
                    `${this.$apiUrls.hanqmedi}/getProducts`,
                    requestData
                );

                if (response.data.code === 200 && response.data.contentsList) {
                    const categoryKey = categoryId.toString();
                    const products = response.data.contentsList || [];

                    if (page === 1) {
                        this.productsByCategory[categoryKey] = products;
                    } else {
                        this.productsByCategory[categoryKey] = [
                            ...(this.productsByCategory[categoryKey] || []),
                            ...products
                        ];
                    }

                    // 전체 재고 배열에도 추가
                    if (addToAll) {
                        if (page === 1) {
                            // 기존 카테고리 재고 제거 후 새로 추가
                            this.allProducts = this.allProducts.filter(p =>
                                !products.some(newP => newP.num === p.num)
                            );
                        }
                        this.allProducts = [...this.allProducts, ...products];
                        // 중복 제거
                        this.allProducts = this.allProducts.filter((product, index, self) =>
                            index === self.findIndex(p => p.num === product.num)
                        );
                    }

                    this.currentPage = response.data.currentPage || 1;
                    this.totalPages = response.data.totalPages || 1;
                } else {
                    console.error('API 응답 실패:', response.data);
                }
            } catch (error) {
                console.error('재고 로드 실패:', error);
                this.$swal.fire({
                    icon: 'error',
                    title: '오류',
                    text: error.response?.data?.message || '재고를 불러오는 중 오류가 발생했습니다.'
                });
            } finally {
                if (page === 1) {
                    this.loadingProducts = false;
                }
            }
        },
    },

    async mounted() {
        if (this.categoryList.length > 0) {
            await this.loadAllProducts();
        }
    }
}
</script>